import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { FileDownloadOutlined as ExportIcon } from "@mui/icons-material";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import { toast } from "react-hot-toast";
import { PaginatedTable } from "../../../components";
import { baseAxios } from "../../../utils/config";

function compareRows(a, b, field) {
  const va = a[field];
  const vb = b[field];
  if (typeof va === "number" || typeof vb === "number") {
    return (Number(va) || 0) - (Number(vb) || 0);
  }
  return String(va ?? "").localeCompare(String(vb ?? ""), undefined, {
    sensitivity: "base",
  });
}

/**
 * Formula Performance analytics table
 */
export function PerformanceTab({
  salon,
  startDate,
  endDate,
  formatDateToISO,
  user,
  needsLocation,
  refreshKey,
  hasRun,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("usage_count");
  const [sortOrder, setSortOrder] = useState("desc");

  const loadData = useCallback(async () => {
    if (needsLocation || !salon) {
      setRows([]);
      return;
    }

    setLoading(true);
    try {
      const params = {
        locationId: salon?.location_id || user.location_id,
      };
      if (startDate) params.startDate = formatDateToISO(startDate);
      if (endDate) params.endDate = formatDateToISO(endDate);

      const response = await baseAxios.get(
        "/formula-tracker/analytics/performance",
        { params }
      );
      setRows(Array.isArray(response.data) ? response.data : []);
    } catch {
      setRows([]);
      toast.error("Failed to load formula performance data", { duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, [
    endDate,
    formatDateToISO,
    needsLocation,
    salon,
    startDate,
    user.location_id,
  ]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshKey]);

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const c = compareRows(a, b, sortBy);
      return sortOrder === "asc" ? c : -c;
    });
    return copy;
  }, [rows, sortBy, sortOrder]);

  const handleSortChange = useCallback((field, order) => {
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const noopPagination = useCallback(() => {}, []);

  const handleExport = useCallback(async () => {
    if (!sortedRows.length) {
      toast.error("No data to export", { duration: 2500 });
      return;
    }
    try {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Formula Performance");
      worksheet.columns = [
        { header: "Formula/Recipe", key: "recipe_name", width: 30 },
        { header: "Times Used", key: "usage_count", width: 12 },
        { header: "Unique Clients", key: "unique_clients", width: 14 },
        { header: "Repeat Bookings", key: "repeat_bookings", width: 14 },
        { header: "Repeat Rate", key: "repeat_percentage", width: 12 },
        { header: "Avg Products", key: "avg_products", width: 12 },
      ];
      worksheet.getRow(1).font = { bold: true };
      sortedRows.forEach((row) => worksheet.addRow(row));
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `FormulaPerformance_${dayjs().format("YYYY-MM-DD")}.xlsx`
      );
    } catch {
      toast.error("Export failed", { duration: 3000 });
    }
  }, [sortedRows]);

  const columns = useMemo(
    () => [
      {
        id: "recipe_name",
        label: "Formula/Recipe",
        field: "recipe_name",
        minWidth: 220,
        render: (v) => v || "—",
      },
      {
        id: "usage_count",
        label: "Times Used",
        field: "usage_count",
        minWidth: 110,
        align: "center",
        render: (v) => Number(v) || 0,
      },
      {
        id: "unique_clients",
        label: "Unique Clients",
        field: "unique_clients",
        minWidth: 130,
        align: "center",
        render: (v) => Number(v) || 0,
      },
      {
        id: "repeat_bookings",
        label: "Repeat Bookings",
        field: "repeat_bookings",
        minWidth: 140,
        align: "center",
        render: (v) => Number(v) || 0,
      },
      {
        id: "repeat_percentage",
        label: "Repeat Rate",
        field: "repeat_percentage",
        minWidth: 120,
        align: "center",
        render: (v) =>
          v == null || Number.isNaN(Number(v))
            ? "0.0%"
            : `${Number(v).toFixed(1)}%`,
      },
      {
        id: "avg_products",
        label: "Avg Products",
        field: "avg_products",
        minWidth: 120,
        align: "center",
        render: (v) =>
          v == null || Number.isNaN(Number(v)) ? "0.0" : Number(v).toFixed(1),
      },
    ],
    []
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          mb: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Typography fontWeight={600}>Top Performing Formulas</Typography>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<ExportIcon />}
          onClick={handleExport}
          disabled={loading || !sortedRows.length}
          sx={{ textTransform: "none", fontWeight: 600, minHeight: 36 }}
        >
          Export
        </Button>
      </Box>
      <PaginatedTable
        columns={columns}
        rows={sortedRows}
        totalRowCount={sortedRows.length}
        loading={loading}
        onPaginationChange={noopPagination}
        onSortChange={handleSortChange}
        initialOrderBy="usage_count"
        initialOrder="desc"
        searchQuery={`perf|${refreshKey}|${salon?.location_id}|${formatDateToISO(startDate)}|${formatDateToISO(endDate)}`}
        clientSidePagination
        skeletonHeight="40px"
        getRowKey={(row, i) => row.formula_id ?? `formula-${i}`}
        emptyMessage={
          hasRun
            ? "No formula performance data for this range"
            : "Click Load Analytics to view data"
        }
      />
    </Box>
  );
}
